import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Send, Loader2, FileText, Mail, ExternalLink, CheckCircle2, XCircle, Clock } from "lucide-react";

/**
 * Compose + send a personalised pitch deck (PPTX) to a dealer prospect.
 *
 * Hits POST /pitch/send (in routes/pitch.py) which builds a personalised
 * .pptx via build_pitch_deck.build_deck() and emails it via IONOS, logging
 * the send to db.pitch_log. The recent-sends table below uses GET /pitch/log.
 *
 * Visible only to platform_owner accounts on the /accounts page — that's
 * where outbound sales activity lives in this product.
 */
const STATUS_META = {
  sent: { icon: Send, colour: "text-primary", label: "Sent" },
  opened: { icon: Mail, colour: "text-primary", label: "Opened" },
  called: { icon: Clock, colour: "text-warning", label: "Called" },
  meeting_booked: { icon: CheckCircle2, colour: "text-success", label: "Meeting booked" },
  won: { icon: CheckCircle2, colour: "text-success", label: "Won" },
  lost: { icon: XCircle, colour: "text-destructive", label: "Lost" },
  failed: { icon: XCircle, colour: "text-destructive", label: "Failed" },
};

function fmtRelative(iso) {
  if (!iso) return "—";
  try {
    const ago = (Date.now() - new Date(iso).getTime()) / 1000;
    if (ago < 60) return `${Math.floor(ago)}s ago`;
    if (ago < 3600) return `${Math.floor(ago / 60)}m ago`;
    if (ago < 86400) return `${Math.floor(ago / 3600)}h ago`;
    return `${Math.floor(ago / 86400)}d ago`;
  } catch { return iso; }
}

export default function SendPitchDeckCard() {
  const [prospectName, setProspectName] = useState("");
  const [prospectEmail, setProspectEmail] = useState("");
  const [dealershipName, setDealershipName] = useState("");
  const [coverNote, setCoverNote] = useState("");
  const [sending, setSending] = useState(false);
  const [draft, setDraft] = useState(null);
  const [recent, setRecent] = useState([]);
  const [loadingLog, setLoadingLog] = useState(true);

  const loadLog = async () => {
    setLoadingLog(true);
    try {
      const r = await api.get("/pitch/log", { params: { limit: 8 } });
      setRecent(r?.data?.items || []);
    } catch {
      setRecent([]);
    } finally {
      setLoadingLog(false);
    }
  };

  useEffect(() => { loadLog(); }, []);

  const send = async (e) => {
    e?.preventDefault?.();
    const name = prospectName.trim();
    const email = prospectEmail.trim();
    const dealer = dealershipName.trim();
    if (!name || !email || !dealer) {
      toast.error("Need a name, an email and a dealership.");
      return;
    }
    setSending(true);
    try {
      const r = await api.post("/pitch/prepare", {
        prospect_name: name,
        prospect_email: email,
        dealership_name: dealer,
        cover_note: coverNote.trim() || null,
      });
      setDraft(r.data);
      toast.success("Deck built — check the preview before it's emailed.");
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || "Couldn't build the deck.";
      toast.error(typeof msg === "string" ? msg : "Couldn't build the deck.");
    } finally {
      setSending(false);
    }
  };

  const confirmSend = async () => {
    if (!draft) return;
    setSending(true);
    try {
      const r = await api.post(`/pitch/confirm/${draft.send_id}`);
      if (r?.data?.ok) {
        toast.success(`Deck sent to ${prospectName.trim()} at ${dealershipName.trim()}`);
        setProspectName(""); setProspectEmail(""); setDealershipName(""); setCoverNote("");
        setDraft(null);
        loadLog();
      } else {
        toast.error(r?.data?.email_error || "Email failed — see recent sends below.");
        setDraft(null);
        loadLog();
      }
    } catch (err) {
      const msg = err?.response?.data?.detail || "Couldn't send the deck.";
      toast.error(typeof msg === "string" ? msg : "Couldn't send the deck.");
    } finally {
      setSending(false);
    }
  };

  const discardDraft = async () => {
    if (!draft) return;
    try { await api.delete(`/pitch/draft/${draft.send_id}`); } catch {}
    setDraft(null);
  };

  const updateStatus = async (sendId, nextStatus) => {
    try {
      await api.patch(`/pitch/log/${sendId}`, { status: nextStatus });
      toast.success(`Marked as ${nextStatus.replace("_", " ")}.`);
      loadLog();
    } catch {
      toast.error("Couldn't update status.");
    }
  };

  return (
    <section className="rounded-lg border border-white/10 bg-white/[0.02]" data-testid="send-pitch-deck-card">
      <div className="px-5 py-4 border-b border-white/10 flex items-center gap-3">
        <Send className="w-5 h-5 text-primary" />
        <div>
          <h2 className="font-display font-bold text-lg" data-testid="send-pitch-deck-title">Send a personalised pitch deck</h2>
          <p className="text-xs text-white/50 mt-0.5">
            Build + email the boardroom PPTX to a dealer in one click. The deck is auto-personalised with the dealership name.
          </p>
        </div>
      </div>

      <form onSubmit={send} className="p-5 grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="text-[11px] font-bold uppercase tracking-wider text-white/50">Prospect name</label>
          <input
            type="text"
            value={prospectName}
            onChange={(e) => setProspectName(e.target.value)}
            placeholder="e.g. Mark Hopkinson"
            className="mt-1 w-full px-3 py-2 rounded-md bg-white/5 border border-white/10 text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-primary"
            data-testid="pitch-prospect-name"
            disabled={sending}
            maxLength={120}
            required
          />
        </div>
        <div>
          <label className="text-[11px] font-bold uppercase tracking-wider text-white/50">Prospect email</label>
          <input
            type="email"
            value={prospectEmail}
            onChange={(e) => setProspectEmail(e.target.value)}
            placeholder="e.g. mark.hopkinson@dealership.co.uk"
            className="mt-1 w-full px-3 py-2 rounded-md bg-white/5 border border-white/10 text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-primary"
            data-testid="pitch-prospect-email"
            disabled={sending}
            required
          />
        </div>
        <div className="md:col-span-2">
          <label className="text-[11px] font-bold uppercase tracking-wider text-white/50">Dealership name</label>
          <input
            type="text"
            value={dealershipName}
            onChange={(e) => setDealershipName(e.target.value)}
            placeholder="e.g. Toyota Service Centre Manchester"
            className="mt-1 w-full px-3 py-2 rounded-md bg-white/5 border border-white/10 text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-primary"
            data-testid="pitch-dealership-name"
            disabled={sending}
            maxLength={120}
            required
          />
          <p className="text-[10.5px] text-white/40 mt-1">
            Cover slide + closing slide auto-stamp with this name.
          </p>
        </div>
        <div className="md:col-span-2">
          <label className="text-[11px] font-bold uppercase tracking-wider text-white/50">Cover note (optional)</label>
          <textarea
            value={coverNote}
            onChange={(e) => setCoverNote(e.target.value)}
            placeholder="A line or two that sits at the top of the email — &quot;Great to meet you at the Toyota dealer day&hellip;&quot;"
            rows={3}
            className="mt-1 w-full px-3 py-2 rounded-md bg-white/5 border border-white/10 text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-primary resize-none"
            data-testid="pitch-cover-note"
            disabled={sending}
            maxLength={1200}
          />
        </div>
        <div className="md:col-span-2 flex items-center justify-between gap-3 pt-1">
          <div className="text-[11px] text-white/40">
            Sends via IONOS · <span className="font-mono">Info@servicesellpro.co.uk</span>
          </div>
          {!draft && (
            <button
              type="submit"
              disabled={sending}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-primary text-primary-foreground font-bold text-sm hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              data-testid="pitch-send-btn"
            >
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />}
              {sending ? "Building deck…" : "Build deck & preview"}
            </button>
          )}
        </div>

        {draft && (
          <div className="md:col-span-2 rounded-md border border-primary/40 bg-primary/5 p-4 space-y-3" data-testid="pitch-preview-panel">
            <div className="flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4 text-success" />
              <span className="text-sm font-bold">Deck built — review before sending</span>
            </div>
            <div className="text-xs text-white/70 space-y-1">
              <div>Cover: <span className="text-white font-semibold">"{draft.stamps?.cover}"</span></div>
              <div>Closing slide: <span className="text-white font-semibold">"{draft.stamps?.close}"</span></div>
              <div>{draft.slides} slides · {draft.pictures} product screenshots embedded</div>
              {draft.pictures === 0 && (
                <div className="text-destructive font-bold" data-testid="pitch-preview-no-images-warning">
                  ⚠ No screenshots embedded — do not send. Contact support.
                </div>
              )}
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <a
                href={`/api/pitch/preview/${draft.send_id}`}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 px-3 py-2 rounded-md border border-white/20 text-sm font-semibold hover:bg-white/5 transition-colors"
                data-testid="pitch-preview-download-btn"
              >
                <FileText className="w-4 h-4" /> Open the deck
              </a>
              <button
                type="button"
                onClick={confirmSend}
                disabled={sending}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-primary text-primary-foreground font-bold text-sm hover:bg-primary/90 disabled:opacity-50 transition-colors"
                data-testid="pitch-confirm-send-btn"
              >
                {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                {sending ? "Sending…" : `Looks good — send to ${prospectEmail.trim()}`}
              </button>
              <button
                type="button"
                onClick={discardDraft}
                disabled={sending}
                className="text-sm text-white/50 hover:text-destructive px-2 py-2 transition-colors"
                data-testid="pitch-discard-draft-btn"
              >
                Discard
              </button>
            </div>
          </div>
        )}
      </form>

      {/* Recent sends — pitch log */}
      <div className="border-t border-white/10 px-5 py-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-[11px] font-bold uppercase tracking-wider text-white/60">Recent sends</h3>
          {recent.length > 0 && (
            <span className="text-[10px] font-mono text-white/40">{recent.length} shown</span>
          )}
        </div>
        {loadingLog ? (
          <div className="text-sm text-white/40 py-4 flex items-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading…
          </div>
        ) : recent.length === 0 ? (
          <div className="text-sm text-white/50 py-3" data-testid="pitch-log-empty">
            No decks sent from this workshop yet. Your first one will appear here.
          </div>
        ) : (
          <ul className="divide-y divide-white/5" data-testid="pitch-log-list">
            {recent.map((row) => {
              const meta = STATUS_META[row.status] || STATUS_META.sent;
              const I = meta.icon;
              return (
                <li key={row.id} className="py-3 flex items-start gap-3" data-testid={`pitch-log-row-${row.id}`}>
                  <I className={`w-4 h-4 mt-0.5 shrink-0 ${meta.colour}`} />
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-baseline gap-x-2">
                      <span className="font-semibold text-sm">{row.dealership_name}</span>
                      <span className="text-xs text-white/60">· {row.prospect_name}</span>
                      <span className="text-[11px] font-mono text-white/40">{row.prospect_email}</span>
                    </div>
                    <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                      <span className={`text-[10px] font-bold uppercase tracking-wider ${meta.colour}`}>{meta.label}</span>
                      <span className="text-[11px] text-white/40">· {fmtRelative(row.created_at)}</span>
                      {row.email_error && (
                        <span className="text-[10.5px] text-destructive truncate" title={row.email_error}>
                          · {row.email_error.slice(0, 60)}
                        </span>
                      )}
                    </div>
                    {/* Follow-up actions */}
                    {row.status !== "won" && row.status !== "lost" && (
                      <div className="flex items-center gap-1.5 mt-2 flex-wrap">
                        {row.status !== "called" && (
                          <button
                            onClick={() => updateStatus(row.id, "called")}
                            className="text-[10.5px] px-2 py-0.5 rounded border border-white/15 text-white/70 hover:bg-white/5 transition-colors"
                            data-testid={`pitch-log-mark-called-${row.id}`}
                          >Mark called</button>
                        )}
                        {row.status !== "meeting_booked" && (
                          <button
                            onClick={() => updateStatus(row.id, "meeting_booked")}
                            className="text-[10.5px] px-2 py-0.5 rounded border border-white/15 text-white/70 hover:bg-white/5 transition-colors"
                            data-testid={`pitch-log-mark-meeting-${row.id}`}
                          >Meeting booked</button>
                        )}
                        <button
                          onClick={() => updateStatus(row.id, "won")}
                          className="text-[10.5px] px-2 py-0.5 rounded border border-success/40 text-success hover:bg-success/10 transition-colors"
                          data-testid={`pitch-log-mark-won-${row.id}`}
                        >Won</button>
                        <button
                          onClick={() => updateStatus(row.id, "lost")}
                          className="text-[10.5px] px-2 py-0.5 rounded border border-destructive/40 text-destructive hover:bg-destructive/10 transition-colors"
                          data-testid={`pitch-log-mark-lost-${row.id}`}
                        >Lost</button>
                      </div>
                    )}
                  </div>
                  {row.deck_filename && (
                    <a
                      href={`/api/pitch/preview/${row.id}`}
                      target="_blank"
                      rel="noreferrer"
                      className="shrink-0 text-[10.5px] font-mono text-white/50 hover:text-primary inline-flex items-center gap-1 transition-colors"
                      data-testid={`pitch-log-download-${row.id}`}
                      title={row.deck_filename}
                    >
                      <FileText className="w-3 h-3" /> deck <ExternalLink className="w-3 h-3" />
                    </a>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </section>
  );
}
